import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CalendarCheck2, ClipboardList, FileText, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { bookingApi } from '../../api/bookingApi';
import { ClientLayout } from '../../components/layouts';
import { useAuth } from '../../context/AuthContext';
import { activeClientStatuses, formatClientDate, formatClientMoney, getClientStatusMeta } from './clientHelpers';

const ClientDashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    bookingApi.myBookings()
      .then((response) => { if (!cancelled) setBookings(response.data || []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || "Couldn't load your bookings."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => ({
    total: bookings.length,
    active: bookings.filter((booking) => activeClientStatuses.has(booking.status)).length,
    completed: bookings.filter((booking) => booking.status === 'completed').length,
    invoices: bookings.filter((booking) => booking.invoiceAvailable).length,
  }), [bookings]);

  const recentBookings = bookings.slice(0, 5);

  return (
    <ClientLayout title="Dashboard">
      <div className="mx-auto w-full max-w-[1400px]">
        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-giggs-green">Client portal</p>
            <h2 className="mb-0 mt-2 font-nunito text-[clamp(1.65rem,3vw,2.25rem)] font-black tracking-[-0.025em] text-giggs-ink">Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h2>
            <p className="mb-0 mt-2 max-w-2xl text-sm leading-6 text-giggs-muted">Manage bookings, tracking numbers and invoices from one place.</p>
          </div>
          <Link to="/book" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-giggs-green px-5 py-3 text-sm font-bold text-white no-underline shadow-sm transition hover:bg-giggs-green-hover">
            <PlusCircle size={17} /> New booking
          </Link>
        </div>

        {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: 'Total bookings', value: stats.total, icon: ClipboardList },
            { label: 'Active bookings', value: stats.active, icon: CalendarCheck2 },
            { label: 'Completed', value: stats.completed, icon: CalendarCheck2 },
            { label: 'Invoices ready', value: stats.invoices, icon: FileText },
          ].map(({ label, value, icon: Icon }) => (
            <article key={label} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80">
              <div className="flex items-center justify-between gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-giggs-green/10 text-giggs-green"><Icon size={20} /></span>
                <span className="font-nunito text-3xl font-black text-giggs-ink">{loading ? '—' : value}</span>
              </div>
              <p className="mb-0 mt-4 text-sm font-semibold text-giggs-muted">{label}</p>
            </article>
          ))}
        </div>

        <div className="mt-7 grid gap-6 xl:grid-cols-[1.45fr_0.75fr]">
          <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80">
            <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
              <div><h3 className="m-0 font-nunito text-lg font-bold text-giggs-ink">Recent bookings</h3><p className="mb-0 mt-1 text-xs text-giggs-muted">Latest activity on your Giggs account.</p></div>
              <Link to="/client/bookings" className="inline-flex items-center gap-1 text-xs font-bold text-giggs-green no-underline">View all <ArrowRight size={14} /></Link>
            </div>
            {loading ? (
              <div className="px-5 py-12 text-center text-sm text-giggs-muted">Loading bookings…</div>
            ) : recentBookings.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-giggs-muted">No bookings yet. <Link to="/book" className="font-bold text-giggs-green no-underline">Book a service</Link></div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentBookings.map((booking) => {
                  const meta = getClientStatusMeta(booking.status);
                  return (
                    <Link key={booking._id} to={`/client/track/${booking.trackingId}`} className="flex flex-col gap-3 px-5 py-4 no-underline transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                      <div className="min-w-0">
                        <p className="m-0 truncate text-sm font-bold text-giggs-ink">{booking.service?.name || 'Giggs booking'}</p>
                        <p className="mb-0 mt-1 text-xs text-giggs-muted">{formatClientDate(booking.scheduledDate)} · {booking.startTime || '—'}–{booking.endTime || '—'}</p>
                        <p className="mb-0 mt-1 break-all font-mono text-[11px] text-slate-400">{booking.trackingId}</p>
                      </div>
                      <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${meta.className}`}>{meta.label}</span>
                        <span className="text-sm font-black text-giggs-ink">{formatClientMoney(booking.total)}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="rounded-2xl bg-giggs-dark p-5 text-white shadow-sm sm:p-6">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-giggs-mint">Quick actions</p>
            <div className="mt-4 space-y-2">
              {[
                { label: 'Book a service', to: '/book' },
                { label: 'Track a booking', to: '/client/track' },
                { label: 'View invoices', to: '/client/invoices' },
                { label: 'See all bookings', to: '/client/bookings' },
              ].map((item) => (
                <Link key={item.label} to={item.to} className="flex items-center justify-between rounded-xl bg-white/[0.06] px-4 py-3 text-sm font-semibold text-white/80 no-underline transition hover:bg-white/[0.1] hover:text-white">
                  {item.label}<ArrowRight size={15} />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ClientDashboard;
